import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Check,
  ChevronRight,
  CircleAlert,
  HardDrive,
  LayoutGrid,
  MapPin,
  Video,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../auth/AuthProvider';
import { channelsKey, isRecorder, listChannels, statusView, type Device } from '../inventory/api';
import { DND_DEVICE } from './tiles';

interface Props {
  devices: Device[];
  loading: boolean;
  failed: boolean;
  filtered: boolean;
  /** Live view: cameras already in the mosaic (checked). */
  inMosaic?: Set<string>;
  /** Live view: 1-based tile the next pick goes to. */
  target?: number;
  onPick: (id: string) => void;
  /** Live view: puts a recorder's channels in the mosaic, from the first tile. */
  onShowAll?: (ids: string[]) => void;
  /**
   * Picking one camera (playback): the chosen device is highlighted, and the
   * recorder it belongs to (selectedParent) starts open.
   */
  selected?: string;
  selectedParent?: string | null;
  /** Client and account groups start collapsed unless false. */
  collapsed?: boolean;
}

const noneShown = new Set<string>();

/**
 * Cameras for the live view. DVR/NVR recorders are groups: they expand into
 * their enabled channels (loaded on first open), which are the cameras.
 */
export function CameraTree({
  devices,
  loading,
  failed,
  filtered,
  inMosaic = noneShown,
  selectedParent,
  collapsed = true,
  ...rest
}: Props) {
  if (loading)
    return (
      <p role="status" className="px-1 py-4 text-[13px] text-fg-muted">
        Carregando câmeras…
      </p>
    );
  if (failed)
    return (
      <p role="alert" className="flex items-center gap-2 px-1 py-4 text-[13px] text-crit">
        <CircleAlert aria-hidden="true" className="size-4" />
        Não foi possível carregar as câmeras.
      </p>
    );
  if (devices.length === 0)
    return (
      <p className="px-1 py-4 text-[13px] text-fg-muted">
        {filtered ? (
          'Nenhuma câmera corresponde ao filtro.'
        ) : (
          <>
            Nenhum dispositivo cadastrado.{' '}
            <Link to="/devices" className="text-accent hover:underline">
              Cadastrar em Dispositivos
            </Link>
          </>
        )}
      </p>
    );
  // Groups start closed, except the path to the selected camera (playback)
  // and while searching (the matches are shown).
  const holdsSelection = (d: Device) =>
    !collapsed ||
    filtered ||
    (rest.selected !== undefined && (d.id === rest.selected || d.id === selectedParent));
  const item = (d: Device) =>
    isRecorder(d) ? (
      <RecorderNode
        key={d.id}
        recorder={d}
        inMosaic={inMosaic}
        startOpen={selectedParent === d.id}
        {...rest}
      />
    ) : (
      <li key={d.id}>
        <CameraButton device={d} shown={inMosaic.has(d.id)} {...rest} />
      </li>
    );
  return (
    <ul
      aria-label="Clientes e contas"
      className="divide-y divide-line overflow-hidden rounded-md border border-line bg-surface"
    >
      {groupByClient(devices).map((c) => (
        <Group
          key={c.id}
          kind="cliente"
          name={c.name}
          count={c.accounts.length}
          unit="conta"
          startOpen={c.accounts.some((a) => a.devices.some(holdsSelection))}
        >
          {c.accounts.map((a) => (
            <Group
              key={a.id}
              kind="conta"
              name={a.name}
              count={a.devices.length}
              unit="dispositivo"
              nested
              startOpen={a.devices.some(holdsSelection)}
            >
              {a.devices.map(item)}
            </Group>
          ))}
        </Group>
      ))}
    </ul>
  );
}

interface AccountGroup {
  id: string;
  name: string;
  devices: Device[];
}

/** Client → account → devices, in the order the devices come (ADR-026). */
function groupByClient(devices: Device[]) {
  const clients = new Map<
    string,
    { id: string; name: string; accounts: Map<string, AccountGroup> }
  >();
  for (const d of devices) {
    const cid = d.client?.id ?? d.site.id;
    const c = clients.get(cid) ?? {
      id: cid,
      name: d.client?.name ?? d.site.name,
      accounts: new Map<string, AccountGroup>(),
    };
    const a = c.accounts.get(d.site.id) ?? { id: d.site.id, name: d.site.name, devices: [] };
    a.devices.push(d);
    c.accounts.set(d.site.id, a);
    clients.set(cid, c);
  }
  return [...clients.values()].map((c) => ({
    id: c.id,
    name: c.name,
    accounts: [...c.accounts.values()],
  }));
}

/** A collapsible client or account group of the tree. */
function Group({
  kind,
  name,
  count,
  unit,
  nested = false,
  startOpen = false,
  children,
}: {
  kind: 'cliente' | 'conta';
  name: string;
  count: number;
  unit: string;
  nested?: boolean;
  startOpen?: boolean;
  children: ReactNode;
}) {
  // Opens by itself when it comes to hold the selection (or a search
  // match) until the operator toggles it.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? startOpen;
  const Icon = kind === 'cliente' ? Building2 : MapPin;
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${open ? 'Recolher' : 'Expandir'} ${kind} ${name}`}
        onClick={() => {
          setToggled(!open);
        }}
        className={`flex w-full items-center gap-2 py-1.5 pr-2 text-left hover:bg-surface-hover ${
          nested ? 'pl-5 text-xs text-fg-muted' : 'bg-canvas pl-2 text-[13px] font-medium text-fg'
        }`}
      >
        <ChevronRight
          aria-hidden="true"
          className={`size-3.5 shrink-0 text-fg-subtle transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <Icon aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
        <span className="truncate">{name}</span>
        <span className="ml-auto shrink-0 text-2xs font-normal text-fg-subtle">
          {count} {count === 1 ? unit : `${unit}s`}
        </span>
      </button>
      {open && (
        <ul
          aria-label={`${kind === 'cliente' ? 'Contas' : 'Dispositivos'} de ${name}`}
          className={`divide-y divide-line border-t border-line ${nested ? 'pl-5' : ''}`}
        >
          {children}
        </ul>
      )}
    </li>
  );
}

type NodeProps = Pick<Props, 'target' | 'onPick' | 'onShowAll' | 'selected'>;

function RecorderNode({
  recorder,
  inMosaic,
  startOpen,
  ...rest
}: NodeProps & { recorder: Device; inMosaic: Set<string>; startOpen: boolean }) {
  const { request } = useAuth();
  // Follows the selected camera until the operator opens or closes it.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? startOpen;
  const channels = useQuery({
    queryKey: channelsKey(recorder.id),
    queryFn: () => listChannels(request, recorder.id),
    enabled: open,
    refetchInterval: 15_000,
  });
  // Disabled channels are not in use: they are not cameras to watch.
  const cameras = (channels.data ?? []).filter((c) => c.enabled);
  const shown = cameras.filter((c) => inMosaic.has(c.id)).length;
  const status = statusView[recorder.status];
  const groupId = `rec-${recorder.id}`;

  return (
    <li>
      <div className="flex items-center hover:bg-surface-hover">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={groupId}
          onClick={() => {
            setToggled(!open);
          }}
          className="flex min-w-0 flex-1 items-center gap-2 py-2 pr-2 pl-1.5 text-left"
        >
          <ChevronRight
            aria-hidden="true"
            className={`size-4 shrink-0 text-fg-subtle transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <HardDrive aria-hidden="true" className="size-4 shrink-0 text-fg-subtle" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-fg">{recorder.name}</span>
            <span className="block truncate text-2xs text-fg-muted">
              {recorder.site.name} · {recorder.channel_count} canais
              {shown > 0 && ` · ${String(shown)} no mosaico`}
            </span>
          </span>
          <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
        </button>
      </div>
      {open && (
        <div id={groupId} className="border-t border-line bg-canvas/40">
          {channels.isPending ? (
            <p role="status" className="py-2 pr-3 pl-10 text-xs text-fg-muted">
              Carregando canais…
            </p>
          ) : channels.isError ? (
            <p role="alert" className="py-2 pr-3 pl-10 text-xs text-crit">
              Não foi possível carregar os canais.
            </p>
          ) : cameras.length === 0 ? (
            <p className="py-2 pr-3 pl-10 text-xs text-fg-muted">
              Nenhum canal ativo.{' '}
              <Link to="/devices" className="text-accent hover:underline">
                Ativar em Dispositivos
              </Link>
            </p>
          ) : (
            <>
              <ul aria-label={`Canais de ${recorder.name}`}>
                {cameras.map((c) => (
                  <li key={c.id}>
                    <CameraButton
                      device={c}
                      shown={inMosaic.has(c.id)}
                      recorderName={recorder.name}
                      {...rest}
                    />
                  </li>
                ))}
              </ul>
              {rest.onShowAll && cameras.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    rest.onShowAll?.(cameras.map((c) => c.id));
                  }}
                  className="flex w-full items-center gap-2 border-t border-line py-1.5 pr-3 pl-10 text-left text-xs text-accent hover:bg-surface-hover"
                >
                  <LayoutGrid aria-hidden="true" className="size-3.5" />
                  {cameras.length > 16
                    ? 'Exibir os 16 primeiros no mosaico'
                    : `Exibir os ${String(cameras.length)} no mosaico`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

function CameraButton({
  device: d,
  shown,
  recorderName,
  target,
  onPick,
  selected,
}: NodeProps & { device: Device; shown: boolean; recorderName?: string }) {
  const status = statusView[d.status];
  const picking = selected !== undefined;
  const current = picking && selected === d.id;
  const indent = recorderName !== undefined;
  // Under its recorder, "<recorder> — Canal 3" reads as "Canal 3".
  const prefix = `${recorderName ?? ''} — `;
  const title = indent && d.name.startsWith(prefix) ? d.name.slice(prefix.length) : d.name;
  const detail = indent
    ? title === `Canal ${String(d.channel)}`
      ? ''
      : `Canal ${String(d.channel)}`
    : d.site.name;
  return (
    <button
      type="button"
      draggable={!picking}
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_DEVICE, d.id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      {...(target !== undefined ? { title: `Exibir na posição ${String(target)}` } : {})}
      aria-current={current || undefined}
      onClick={() => {
        onPick(d.id);
      }}
      className={`flex w-full items-center gap-3 py-2 pr-3 text-left transition-colors hover:bg-surface-hover ${indent ? 'pl-10' : 'pl-3'} ${current ? 'bg-accent-soft' : ''}`}
    >
      {shown ? (
        <Check aria-label="No mosaico" className="size-4 shrink-0 text-accent" />
      ) : (
        <Video aria-hidden="true" className="size-4 shrink-0 text-fg-subtle" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-fg">{title}</span>
        {detail && <span className="block truncate text-2xs text-fg-muted">{detail}</span>}
      </span>
      {d.enabled ? (
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      ) : (
        <StatusBadge tone="neutral">Desativado</StatusBadge>
      )}
    </button>
  );
}
